import { inject, Injectable } from '@angular/core';
import { Division } from '@cp/shared/data-access/shared/enums';
import { Store } from '@ngrx/store';
import { CompanyService } from '@cp/shared/data-access/company/company.service';
import { map, Observable, skipWhile } from 'rxjs';
import { UsefulLinkConfig } from '@cp/shared/core/interfaces/useful-links.interface';
import { SharedSelectors } from '@cp/shared/data-access/+state/selectors';

@Injectable({ providedIn: 'root' })
export class UsefulLinksService {
  private _companyService: CompanyService = inject(CompanyService);
  private _store: Store = inject(Store);

  public getUsefulLinksConfig(): Observable<UsefulLinkConfig> {
    const qnUrl = this._companyService.qnUrl('pwa');

    return this._store.select(SharedSelectors.selectStaffDetails).pipe(
      skipWhile((status) => !status.isLoaded),
      map(({ division, hasNmcSkill, hasNmbiSkill }) =>
        this._generateConfig(qnUrl, division, hasNmcSkill, hasNmbiSkill),
      ),
    );
  }

  private _generateConfig(
    qnUrl: string,
    division: Division,
    hasNmcSkill: boolean,
    hasNmbiSkill: boolean,
  ): UsefulLinkConfig {
    const config: UsefulLinkConfig = [];
    config.push({
      icon: 'domain',
      title: 'Our offices',
      isExternal: false,
      route: '/help-and-support/contact-us',
    });
    config.push({
      icon: 'feedback',
      title: 'Complaints and incidents',
      isExternal: false,
      route: '/resources',
      params: { modal: 'complaints-and-incidents' },
    });
    if (hasNmcSkill) {
      config.push({
        icon: 'clinical_notes',
        title: 'NMC Scheme',
        isExternal: false,
        route: '/resources/nmc-nmbi-terms-and-conditions',
        params: { schema: 'NMC' },
      });
    }
    if (hasNmbiSkill) {
      config.push({
        icon: 'user_attributes',
        title: 'NMBI Scheme',
        isExternal: false,
        route: '/resources/nmc-nmbi-terms-and-conditions',
        params: { schema: 'NMBI' },
      });
    }
    config.push({
      icon: 'person_alert',
      title: 'Cautions and Convictions',
      isExternal: false,
      route: '/profile',
      params: { modal: 'cautions-and-convictions' },
    });
    config.push({
      icon: 'contact_page',
      title: 'References',
      isExternal: false,
      route: '/profile',
    });
    config.push({
      icon: 'payments',
      title: 'Pay Rates',
      isExternal: false,
      route: '/resources',
      fragment: 'pay-rates',
    });
    config.push({
      icon: 'settings',
      title: 'Settings',
      isExternal: true,
      url: `${qnUrl}/settings`,
    });
    config.push({
      icon: 'group_add',
      title: 'Refer a friend',
      isExternal: true,
      url: `${division === Division.tns ? 'https://thornbury-nursing.com/working-for-us/refer-a-friend/' : 'https://nursingguild.com/working-for-us/refer-a-friend/'}`,
    });
    config.push({
      icon: 'assignment',
      title: 'Quick Nurse',
      isExternal: true,
      url: qnUrl,
    });
    config.push({
      icon: 'web',
      title: 'Thornbury Nursing Services',
      isExternal: true,
      url: `https://thornbury-nursing.com/`,
    });
    config.push({
      icon: 'web',
      title: 'Scottish Nursing Guild',
      isExternal: true,
      url: `https://nursingguild.com/`,
    });
    return config;
  }
}
